import React, { useState } from 'react';
import { Button, Modal } from '@vacasa/react-components-lib';
import { Alert } from '@mui/material';
import { useRepublishPalReviewsCountMutation, useRepublishPalReviewsMutation } from '../../services/ReviewsService';
import type { RepublishPalReviewsCountResponse } from '../../types/review-service.type';
import './ExternalSync.scss';

export const ExternalSync: React.FC = () => {
    const [adminUnitId, setAdminUnitId] = useState('');
    const [reviewDateFrom, setReviewDateFrom] = useState('');
    const [reviewDateTo, setReviewDateTo] = useState('');
    const [message, setMessage] = useState<string>('');
    const [isError, setIsError] = useState(false);
    const [isConfirmSyncAllOpen, setIsConfirmSyncAllOpen] = useState(false);
    const [republishPalReviewsCount, { isLoading: isCounting }] = useRepublishPalReviewsCountMutation();
    const [republishPalReviews, { isLoading: isPublishing }] = useRepublishPalReviewsMutation();
    const isLoading = isCounting || isPublishing;

    const handleAdminUnitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAdminUnitId(event.target.value.replace(/\D/g, ''));
    };

    const onSync = async (withFilters: boolean) => {
        try {
            setIsError(false);
            setMessage('');
            const unit_id = adminUnitId.trim();
            const result = (await republishPalReviewsCount({
                admin_unit_id: withFilters && unit_id !== '' ? +unit_id : undefined,
                review_date_from: withFilters && reviewDateFrom.trim() !== '' ? reviewDateFrom : undefined,
                review_date_to: withFilters && reviewDateTo.trim() !== '' ? reviewDateTo : undefined,
            }).unwrap()) as RepublishPalReviewsCountResponse;

            const parsedToSync = Number(result.to_sync);
            const toSync = Number.isFinite(parsedToSync) ? parsedToSync : 0;
            if (toSync === 0) {
                setMessage('No reviews found for sync with the selected filters.');
                return;
            }

            void republishPalReviews({
                admin_unit_id: withFilters && unit_id !== '' ? +unit_id : undefined,
                review_date_from: withFilters && reviewDateFrom.trim() !== '' ? reviewDateFrom : undefined,
                review_date_to: withFilters && reviewDateTo.trim() !== '' ? reviewDateTo : undefined,
            })
                .unwrap()
                .catch(() => {
                    setIsError(true);
                    setMessage('Unable to start sync. Please try again.');
                });

            setMessage(`Sync started successfully. ${toSync} reviews to emit.`);
        } catch (_e) {
            setIsError(true);
            setMessage('Unable to run sync. Please validate filters and try again.');
        }
    };

    return (
        <div className="external-sync">
            <div className="external-sync-header">
                <h3>External Reviews Sync</h3>
                <p>Use this tool to re-publish PAL reviews.</p>
            </div>

            {message && <Alert severity={isError ? 'error' : 'success'}>{message}</Alert>}

            <div className="external-sync-actions">
                <Button variant="secondary" onClick={() => setIsConfirmSyncAllOpen(true)} disabled={isLoading}>
                    Sync all
                </Button>
            </div>

            <div className="filters">
                <div className="field">
                    <label>
                        Admin Unit ID
                        <input value={adminUnitId} onChange={handleAdminUnitChange} type="text" inputMode="numeric" />
                    </label>
                </div>
                <div className="field">
                    <label>
                        Review From
                        <input
                            type="date"
                            className={reviewDateFrom ? '' : 'date-empty'}
                            value={reviewDateFrom}
                            onChange={(e) => setReviewDateFrom(e.target.value)}
                        />
                    </label>
                </div>
                <div className="field">
                    <label>
                        Review To
                        <input
                            type="date"
                            className={reviewDateTo ? '' : 'date-empty'}
                            value={reviewDateTo}
                            onChange={(e) => setReviewDateTo(e.target.value)}
                        />
                    </label>
                </div>
            </div>

            <div className="external-sync-actions">
                <Button
                    variant="primary"
                    onClick={async () => {
                        await onSync(true);
                    }}
                    disabled={isLoading}
                >
                    Sync with filters
                </Button>
            </div>

            <Modal showModal={isConfirmSyncAllOpen} setShowModal={setIsConfirmSyncAllOpen} size="medium">
                <div className="confirm-sync-title">Confirm full external sync</div>
                <div className="confirm-sync-body">
                    This will re-publish all PAL/PMS external reviews. This action may take time and can generate a high number of events.
                </div>
                <div className="confirm-sync-actions">
                    <Button variant="info" onClick={() => setIsConfirmSyncAllOpen(false)} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={async () => {
                            setIsConfirmSyncAllOpen(false);
                            await onSync(false);
                        }}
                        disabled={isLoading}
                    >
                        Confirm sync all
                    </Button>
                </div>
            </Modal>
        </div>
    );
};
